import { logger } from '../../../utils/logger';
import { getCurrentUserId } from '../../../services/auth';

// userId 未傳入時回退到 getCurrentUserId()（Firebase SDK）
// userId 為空字串時回傳 null，呼叫端應回傳空陣列而非讀取 fallback key
function getStorageKey(userId?: string): string | null {
  const uid = userId !== undefined ? userId : getCurrentUserId();
  return uid ? `orlo-onboarding-seen-tips-${uid}` : null;
}

/**
 * Onboarding Service
 *
 * 負責管理 Tips 的持久化狀態（使用 localStorage）
 * Key 包含 userId，確保多帳號切換時各自獨立
 */
export const onboardingService = {
  /**
   * 取得指定用戶已看過的 Tips
   *
   * @param userId - 用戶 ID；空字串時回傳空陣列（不讀 fallback key，避免污染全局 key）
   * @returns 已看過的 Tip IDs 陣列
   */
  getSeenTipsForUser(userId: string): string[] {
    const key = getStorageKey(userId);
    // userId 為空時 key 為 null，不讀 fallback key（符合 userId 隔離原則）
    if (!key) return [];
    try {
      const stored = localStorage.getItem(key);
      if (!stored || stored === 'null') {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      logger.warn('Failed to load seen tips from localStorage', error);
      return [];
    }
  },

  /**
   * 標記 Tip 為已看過
   *
   * @param tipId - Tip 的唯一識別碼
   * @param userId - 用戶 ID；未傳入時回退到 getCurrentUserId()
   */
  markTipAsSeen(tipId: string, userId?: string): void {
    const uid = userId ?? getCurrentUserId();
    const key = getStorageKey(uid);
    if (!key) return;
    try {
      const existing = this.getSeenTipsForUser(uid);
      if (!existing.includes(tipId)) {
        existing.push(tipId);
        localStorage.setItem(key, JSON.stringify(existing));
      }
    } catch (error) {
      logger.error('Failed to save tip state to localStorage', error);
    }
  },

  /**
   * 重設所有 Tips（開發/測試用）
   *
   * @param userId - 用戶 ID；未傳入時回退到 getCurrentUserId()
   */
  resetAllTips(userId?: string): void {
    const uid = userId ?? getCurrentUserId();
    const key = getStorageKey(uid);
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error('Failed to reset tips in localStorage', error);
    }
  }
};
